import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";
import { ordersAPI } from "../services/api";
import { RefreshCw, ArrowRight, Phone, Wallet, User, MapPin, Package, Star } from "lucide-react";

const STATUS_COLOR = {
  pending:          { bg:"rgba(245,166,35,0.1)",  text:"var(--accent)" },
  needs_assignment: { bg:"rgba(239,68,68,0.1)",   text:"var(--red)" },
  accepted:         { bg:"rgba(59,130,246,0.1)",  text:"#60a5fa" },
  rider_assigned:   { bg:"rgba(168,85,247,0.1)",  text:"#c084fc" },
  picked_up:        { bg:"rgba(245,166,35,0.1)",  text:"var(--accent)" },
  on_the_way:       { bg:"rgba(245,166,35,0.15)", text:"var(--accent)" },
  delivered:        { bg:"rgba(34,197,94,0.1)",   text:"var(--green)" },
  cancelled:        { bg:"rgba(239,68,68,0.1)",   text:"var(--red)" },
};

const PAYMENT_STATUS_COLOR = {
  paid:    { bg:"rgba(34,197,94,0.1)",  text:"var(--green)" },
  unpaid:  { bg:"rgba(239,68,68,0.1)", text:"var(--red)" },
  pending: { bg:"rgba(245,166,35,0.1)", text:"var(--accent)" },
};

function StatusBadge({ status }) {
  const s = STATUS_COLOR[status] || { bg:"var(--bg3)", text:"var(--text2)" };
  return (
    <span style={{ background:s.bg, color:s.text, borderRadius:6, padding:"3px 10px", fontSize:12, fontWeight:600, textTransform:"capitalize" }}>
      {status?.replace(/_/g," ")}
    </span>
  );
}

function PaymentBadge({ status }) {
  const s = PAYMENT_STATUS_COLOR[status] || { bg:"var(--bg3)", text:"var(--text2)" };
  return (
    <span style={{ background:s.bg, color:s.text, borderRadius:6, padding:"3px 10px", fontSize:12, fontWeight:600, textTransform:"capitalize" }}>
      {status}
    </span>
  );
}

function getInitials(name) {
  if (!name) return "?";
  return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
}

function getRoleLabel(role) {
  const map = { customer:"Customer", rider:"Rider", admin:"Admin", branch_admin:"Branch Admin" };
  return map[role] || role;
}

function UserProfileCard({ user }) {
  const walletBalance = parseFloat(user?.wallet_balance || 0).toFixed(2);
  const initials = getInitials(user?.name);

  return (
    <div style={{
      background: "var(--bg2)",
      border: "1px solid var(--border)",
      borderRadius: 20,
      padding: "24px 28px",
      marginBottom: 32,
      position: "relative",
      overflow: "hidden",
    }}>
      {/* Decorative accent blob */}
      <div style={{
        position: "absolute",
        top: -40,
        right: -40,
        width: 180,
        height: 180,
        borderRadius: "50%",
        background: "radial-gradient(circle, rgba(245,166,35,0.08) 0%, transparent 70%)",
        pointerEvents: "none",
      }} />

      <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
        {/* Avatar */}
        <div style={{ position: "relative", flexShrink: 0 }}>
          {user?.avatar_url ? (
            <img
              src={user.avatar_url}
              alt={user.name}
              style={{ width: 64, height: 64, borderRadius: 16, objectFit: "cover", border: "2px solid var(--border2)" }}
            />
          ) : (
            <div style={{
              width: 64,
              height: 64,
              borderRadius: 16,
              background: "linear-gradient(135deg, rgba(245,166,35,0.25) 0%, rgba(245,166,35,0.08) 100%)",
              border: "2px solid rgba(245,166,35,0.3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 22,
              fontWeight: 800,
              color: "var(--accent)",
              letterSpacing: "-1px",
              fontFamily: "var(--font-head, sans-serif)",
            }}>
              {initials}
            </div>
          )}
          {/* Online dot */}
          <div style={{
            position: "absolute",
            bottom: 3,
            right: 3,
            width: 12,
            height: 12,
            borderRadius: "50%",
            background: "var(--green, #22c55e)",
            border: "2px solid var(--bg2)",
          }} />
        </div>

        {/* Name + role + email */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4, flexWrap: "wrap" }}>
            <h2 style={{ fontSize: 20, fontWeight: 800, letterSpacing: "-0.5px", margin: 0 }}>
              {user?.name || "—"}
            </h2>
            <span style={{
              background: "rgba(245,166,35,0.12)",
              color: "var(--accent)",
              borderRadius: 6,
              padding: "2px 9px",
              fontSize: 11,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}>
              {getRoleLabel(user?.role)}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, color: "var(--text2)", display: "flex", alignItems: "center", gap: 5 }}>
              <User size={12} style={{ opacity: 0.5 }} />
              {user?.email || "—"}
            </span>
            <span style={{ fontSize: 13, color: "var(--text2)", display: "flex", alignItems: "center", gap: 5 }}>
              <Phone size={12} style={{ opacity: 0.5 }} />
              {user?.phone || "—"}
            </span>
          </div>
        </div>

        {/* Wallet balance */}
        <div style={{
          background: "linear-gradient(135deg, rgba(245,166,35,0.15) 0%, rgba(245,166,35,0.05) 100%)",
          border: "1px solid rgba(245,166,35,0.25)",
          borderRadius: 14,
          padding: "14px 20px",
          textAlign: "center",
          flexShrink: 0,
          minWidth: 130,
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginBottom: 4 }}>
            <Wallet size={13} style={{ color: "var(--accent)", opacity: 0.8 }} />
            <span style={{ fontSize: 11, color: "var(--accent)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", opacity: 0.8 }}>
              Wallet
            </span>
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "var(--accent)", fontFamily: "var(--font-head, sans-serif)", letterSpacing: "-1px" }}>
            GHS {walletBalance}
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
        gap: 10,
        marginTop: 20,
        paddingTop: 20,
        borderTop: "1px solid var(--border)",
      }}>
        {[
          { icon: <Package size={13}/>, label: "User ID", value: `#${user?.id}` },
          { icon: <MapPin size={13}/>, label: "Branch", value: `Branch ${user?.branch_id || "—"}` },
          { icon: <Star size={13}/>, label: "Account", value: "Active" },
        ].map(({ icon, label, value }) => (
          <div key={label} style={{
            background: "var(--bg3, rgba(255,255,255,0.03))",
            borderRadius: 10,
            padding: "10px 14px",
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 5, color: "var(--text3)", opacity: 0.7 }}>
              {icon}
              <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</span>
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text2)" }}>{value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Orders() {
  const { user } = useAuth();
  const navigate  = useNavigate();
  const [orders,  setOrders]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected,setSelected]= useState(null);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const r = await ordersAPI.myOrders();
      setOrders(r.data.data || []);
    } catch(e) { toast.error("Failed to load orders"); }
    setLoading(false);
  }, [user]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!user && !loading) navigate("/login"); }, [user, loading, navigate]);

  const SERVICE_ICON = { food:"🍔", parcel:"📦", pharmacy:"💊", grocery:"🛒", other:"🚚" };
  const showOTP = (status) => ["rider_assigned", "picked_up", "on_the_way"].includes(status);

  if (!user) return null;

  return (
    <div style={{ minHeight:"100vh", padding:"100px 6% 60px" }}>
      <div style={{ maxWidth:900, margin:"0 auto" }}>

        {/* User Profile Card */}
        <UserProfileCard user={user} />

        {/* Orders Header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:24 }}>
          <div>
            <h1 style={{ fontSize:26, fontWeight:800, letterSpacing:"-1px", marginBottom:2 }}>My Orders</h1>
            <p style={{ fontSize:13, color:"var(--text2)" }}>Track all your deliveries in one place</p>
          </div>
          <div style={{ display:"flex", gap:10 }}>
            <button className="btn-ghost btn-sm" onClick={load}><RefreshCw size={14}/> Refresh</button>
            <button className="btn-primary btn-sm" onClick={() => { window.scrollTo({top:0}); navigate("/"); setTimeout(()=>document.getElementById("order")?.scrollIntoView({behavior:"smooth"}),200); }}>
              ⚡ New Delivery
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:12, padding:"80px 0", color:"var(--text3)" }}>
            <span className="spinner-white" style={{ width:28, height:28, borderTopColor:"var(--accent)" }}/>
            <span>Loading your orders...</span>
          </div>
        ) : orders.length === 0 ? (
          <div style={{ textAlign:"center", padding:"80px 0" }}>
            <div style={{ fontSize:48, marginBottom:16 }}>📭</div>
            <div style={{ fontFamily:"var(--font-head)", fontSize:20, fontWeight:700, marginBottom:8 }}>No orders yet</div>
            <p style={{ color:"var(--text2)", marginBottom:24 }}>Place your first delivery and it will show up here.</p>
            <button className="btn-primary" onClick={() => { navigate("/"); setTimeout(()=>document.getElementById("order")?.scrollIntoView({behavior:"smooth"}),200); }}>
              ⚡ Request a Delivery
            </button>
          </div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            {orders.map(o => (
              <div key={o.id} onClick={() => setSelected(selected?.id===o.id ? null : o)}
                style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:14, padding:20, cursor:"pointer", transition:"border-color 0.2s, transform 0.15s" }}
                onMouseOver={e=>{e.currentTarget.style.borderColor="var(--border2)";e.currentTarget.style.transform="translateY(-1px)";}}
                onMouseOut={e=>{e.currentTarget.style.borderColor="var(--border)";e.currentTarget.style.transform="none";}}>
                <div style={{ display:"flex", alignItems:"center", gap:14, flexWrap:"wrap" }}>
                  <div style={{ width:44, height:44, borderRadius:10, background:"var(--bg3)", border:"1px solid var(--border)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>
                    {SERVICE_ICON[o.service_type] || "🚚"}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:4, flexWrap:"wrap" }}>
                      <span style={{ fontFamily:"var(--font-mono)", fontSize:13, fontWeight:600, color:"var(--accent)" }}>#{o.id}</span>
                      <StatusBadge status={o.status}/>
                      <PaymentBadge status={o.payment_status}/>
                      <span style={{ fontSize:12, color:"var(--text3)", textTransform:"capitalize" }}>{o.service_type}</span>
                    </div>
                    <div style={{ fontSize:13, color:"var(--text2)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                      📍 {o.pickup_address} → 🏁 {o.dropoff_address}
                    </div>
                  </div>
                  <div style={{ textAlign:"right", flexShrink:0 }}>
                    <div style={{ fontFamily:"var(--font-head)", fontSize:16, fontWeight:700, color:"var(--accent)" }}>GHS {parseFloat(o.price||0).toFixed(2)}</div>
                    <div style={{ fontSize:11, color:"var(--text3)", marginTop:2 }}>{o.created_at ? format(new Date(o.created_at),"MMM d, HH:mm") : "—"}</div>
                  </div>
                  <ArrowRight size={16} style={{ color:"var(--text3)", flexShrink:0, transform: selected?.id===o.id ? "rotate(90deg)" : "none", transition:"transform 0.2s" }}/>
                </div>

                {selected?.id === o.id && (
                  <div style={{ marginTop:16, paddingTop:16, borderTop:"1px solid var(--border)", animation:"fadeUp 0.3s ease both" }}>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                      {[
                        ["Pickup",         o.pickup_address],
                        ["Dropoff",        o.dropoff_address],
                        ["Payment Method", o.payment_method],
                        ["Payment Status", o.payment_status],
                        ["Distance",       `${o.distance_km || "—"} km`],
                        ["Rider",          o.rider_name || "Not yet assigned"],
                        ["OTP Verified",   o.otp_verified ? "✅ Yes" : "❌ No"],
                        ["Reference",      o.paystack_reference || "—"],
                      ].map(([l,v]) => (
                        <div key={l}>
                          <div style={{ fontSize:11, color:"var(--text3)", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:3 }}>{l}</div>
                          <div style={{ fontSize:13, color:"var(--text2)" }}>{v}</div>
                        </div>
                      ))}
                      <div>
                        <div style={{ fontSize:11, color:"var(--text3)", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:3 }}>Rider Number</div>
                        {o.rider_phone ? (
                          <a href={`tel:${o.rider_phone}`} onClick={e=>e.stopPropagation()}
                            style={{ fontSize:13, color:"var(--accent)", display:"inline-flex", alignItems:"center", gap:5 }}>
                            <Phone size={12}/> {o.rider_phone}
                          </a>
                        ) : (
                          <div style={{ fontSize:13, color:"var(--text2)" }}>Not yet assigned</div>
                        )}
                      </div>
                      {showOTP(o.status) && (
                        <div>
                          <div style={{ fontSize:11, color:"var(--text3)", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:3 }}>Delivery OTP</div>
                          <div style={{ fontSize:18, fontFamily:"var(--font-mono)", fontWeight:700, color:"var(--accent)", letterSpacing:4 }}>{o.delivery_otp}</div>
                        </div>
                      )}
                    </div>
                    {o.notes && (
                      <div style={{ marginTop:12, background:"var(--bg3)", borderRadius:8, padding:"10px 14px", fontSize:13, color:"var(--text2)" }}>
                        💬 {o.notes}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}